import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { theme as salooneTheme } from '../../../theme';
import PermissionMatrix from './PermissionMatrix';

const { colors } = salooneTheme;

const emptyGrant = () => ({ is_full_access: false, permission_codes: [], is_active: true });

const ManagerAccessForm = ({
  open,
  onClose,
  onSubmit,
  manager = null,
  salons = [],
  eligibleEmployees = [],
  groups = [],
  canGrantManagerManage = false,
  isSaving = false,
  error = '',
}) => {
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    const translated = t(key, { defaultValue: fallback });
    return typeof translated === 'string' ? translated : fallback;
  };
  const [source, setSource] = useState('new');
  const [account, setAccount] = useState({ first_name: '', last_name: '', email: '', phone: '', language: 'fr' });
  const [existingEmployeeId, setExistingEmployeeId] = useState('');
  const [role, setRole] = useState('professional_manager');
  const [employeeSalonId, setEmployeeSalonId] = useState('');
  const [selectedSalonIds, setSelectedSalonIds] = useState([]);
  const [commonGrant, setCommonGrant] = useState(emptyGrant);
  const [grantsBySalon, setGrantsBySalon] = useState({});
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!open) return;
    const managerGrants = manager?.grants || [];
    const firstGrant = managerGrants[0] || emptyGrant();
    setSource('new');
    setAccount({ first_name: '', last_name: '', email: '', phone: '', language: 'fr' });
    setExistingEmployeeId('');
    setRole(manager?.role || 'professional_manager');
    setEmployeeSalonId(manager?.employee_salon_id ? String(manager.employee_salon_id) : '');
    setSelectedSalonIds(managerGrants.map((grant) => Number(grant.salon_id)));
    setCommonGrant({
      is_full_access: Boolean(firstGrant.is_full_access),
      permission_codes: [...(firstGrant.permission_codes || [])],
      is_active: firstGrant.is_active !== false,
    });
    setGrantsBySalon(Object.fromEntries(managerGrants.map((grant) => [Number(grant.salon_id), {
      is_full_access: Boolean(grant.is_full_access),
      permission_codes: [...(grant.permission_codes || [])],
      is_active: grant.is_active !== false,
    }])));
    setValidationError('');
  }, [open, manager]);

  const selectedSalons = useMemo(
    () => salons.filter((salon) => selectedSalonIds.includes(Number(salon.salon_id))),
    [salons, selectedSalonIds],
  );

  const toggleSalon = (salonId) => {
    const numericId = Number(salonId);
    const isSelected = selectedSalonIds.includes(numericId);
    setSelectedSalonIds(isSelected
      ? selectedSalonIds.filter((id) => id !== numericId)
      : [...selectedSalonIds, numericId]);
    setGrantsBySalon((current) => {
      if (isSelected) {
        const next = { ...current };
        delete next[numericId];
        return next;
      }
      return { ...current, [numericId]: { ...commonGrant, permission_codes: [...commonGrant.permission_codes] } };
    });
    if (isSelected && Number(employeeSalonId) === numericId) setEmployeeSalonId('');
  };

  const applyCommonGrant = (nextGrant) => {
    setCommonGrant(nextGrant);
    setGrantsBySalon((current) => Object.fromEntries(selectedSalonIds.map((salonId) => [salonId, {
      ...(current[salonId] || emptyGrant()),
      ...nextGrant,
      permission_codes: [...nextGrant.permission_codes],
    }])));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!manager && source === 'new' && !account.email.trim()) {
      setValidationError(tr('pro_manager_access.errors.email_required', 'Saisissez une adresse e-mail.'));
      return;
    }
    if (!manager && source === 'existing' && !existingEmployeeId) {
      setValidationError(tr('pro_manager_access.errors.employee_required', 'Choisissez un employé.'));
      return;
    }
    if (!selectedSalonIds.length) {
      setValidationError(tr('pro_manager_access.errors.salon_required', 'Choisissez au moins un salon.'));
      return;
    }
    if (role === 'professional_manager_employee' && !selectedSalonIds.includes(Number(employeeSalonId))) {
      setValidationError(tr('pro_manager_access.errors.employee_salon_required', "Choisissez l'unique salon d'activité employé."));
      return;
    }

    const payload = {
      role,
      employee_salon_id: role === 'professional_manager_employee' ? Number(employeeSalonId) : null,
      grants: selectedSalonIds.map((salonId) => {
        const grant = grantsBySalon[salonId] || commonGrant;
        return {
          salon_id: salonId,
          is_full_access: Boolean(grant.is_full_access),
          is_active: grant.is_active !== false,
          permission_codes: [...(grant.permission_codes || [])].sort(),
        };
      }),
    };
    if (!manager) {
      if (source === 'new') payload.account = { ...account, email: account.email.trim() };
      else payload.existing_employee_id = Number(existingEmployeeId);
    }
    onSubmit(payload);
  };

  const sectionTitle = (number, label) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5, mb: 1.25 }}>
      <Box
        component="span"
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: 28,
          height: 28,
          flex: '0 0 auto',
          borderRadius: '50%',
          backgroundColor: colors.primary[100],
          color: colors.primary[800],
          fontSize: '0.8rem',
          fontWeight: 800,
        }}
      >
        {number}
      </Box>
      <Typography variant="h6" sx={{ color: colors.primary[900], fontSize: '1rem', fontWeight: 700 }}>
        {label}
      </Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="md" aria-labelledby="manager-access-title">
      <DialogTitle
        id="manager-access-title"
        sx={{
          color: colors.primary[900],
          fontWeight: 800,
          background: `linear-gradient(135deg, ${colors.primary[50]}, ${colors.secondary[50]})`,
          borderBottom: `1px solid ${colors.primary[100]}`,
        }}
      >
        {manager
          ? tr('pro_manager_access.form.edit_title', 'Modifier les accès')
          : tr('pro_manager_access.form.add_title', 'Ajouter une personne')}
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: colors.primary[100], backgroundColor: colors.background.primary }}>
        <Box component="form" id="manager-access-form" onSubmit={handleSubmit}>
          {!manager && (
            <>
              {sectionTitle(1, tr('pro_manager_access.form.steps.person', 'Choisir la personne'))}
              <RadioGroup row value={source} onChange={(event) => setSource(event.target.value)}>
                <FormControlLabel value="new" control={<Radio />} label={tr('pro_manager_access.form.new_person', 'Nouvelle personne')} />
                <FormControlLabel value="existing" control={<Radio />} label={tr('pro_manager_access.form.existing_employee', 'Employé existant')} />
              </RadioGroup>
              {source === 'new' ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                  <TextField fullWidth label={tr('pro_manager_access.form.first_name', 'Prénom')} value={account.first_name} onChange={(e) => setAccount({ ...account, first_name: e.target.value })} />
                  <TextField fullWidth label={tr('pro_manager_access.form.last_name', 'Nom')} value={account.last_name} onChange={(e) => setAccount({ ...account, last_name: e.target.value })} />
                  <TextField required fullWidth type="email" label={tr('pro_manager_access.form.email', 'E-mail')} value={account.email} onChange={(e) => setAccount({ ...account, email: e.target.value })} />
                </Stack>
              ) : (
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel id="existing-employee-label">{tr('pro_manager_access.form.existing_employee', 'Employé existant')}</InputLabel>
                  <Select labelId="existing-employee-label" label={tr('pro_manager_access.form.existing_employee', 'Employé existant')} value={existingEmployeeId} onChange={(e) => setExistingEmployeeId(e.target.value)}>
                    {eligibleEmployees.map((employee) => (
                      <MenuItem key={employee.id} value={employee.id}>
                        {[employee.first_name, employee.last_name].filter(Boolean).join(' ') || employee.email} — {employee.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}

          {sectionTitle(2, tr('pro_manager_access.form.steps.role', 'Choisir le rôle'))}
          <RadioGroup row value={role} onChange={(event) => setRole(event.target.value)}>
            <FormControlLabel value="professional_manager" control={<Radio />} label={tr('pro_manager_access.roles.professional_manager', 'Gestionnaire')} />
            <FormControlLabel value="professional_manager_employee" control={<Radio />} label={tr('pro_manager_access.roles.professional_manager_employee', 'Gestionnaire + employé')} />
          </RadioGroup>

          {sectionTitle(3, tr('pro_manager_access.form.steps.salons', 'Choisir les salons autorisés'))}
          <FormGroup row>
            {salons.map((salon) => (
              <FormControlLabel key={salon.salon_id} control={<Checkbox checked={selectedSalonIds.includes(Number(salon.salon_id))} onChange={() => toggleSalon(salon.salon_id)} />} label={salon.name} />
            ))}
          </FormGroup>

          {role === 'professional_manager_employee' && (
            <>
              {sectionTitle(4, tr('pro_manager_access.form.steps.employee_salon', "Choisir le salon d'activité employé"))}
              <FormControl fullWidth>
                <InputLabel id="employee-salon-label">{tr('pro_manager_access.form.employee_salon', "Salon d'activité employé")}</InputLabel>
                <Select labelId="employee-salon-label" label={tr('pro_manager_access.form.employee_salon', "Salon d'activité employé")} value={employeeSalonId} onChange={(e) => setEmployeeSalonId(e.target.value)}>
                  {selectedSalons.map((salon) => <MenuItem key={salon.salon_id} value={salon.salon_id}>{salon.name}</MenuItem>)}
                </Select>
              </FormControl>
            </>
          )}

          {sectionTitle(5, tr('pro_manager_access.form.steps.permissions', 'Choisir les droits communs'))}
          <PermissionMatrix groups={groups} value={commonGrant} onChange={applyCommonGrant} canGrantManagerManage={canGrantManagerManage} />

          {selectedSalons.length > 1 && (
            <>
              {sectionTitle(6, tr('pro_manager_access.form.steps.customize', 'Personnaliser par salon (facultatif)'))}
              {selectedSalons.map((salon) => (
                <Accordion key={salon.salon_id} disableGutters sx={{ border: `1px solid ${colors.primary[100]}`, '&:before': { display: 'none' }, mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: colors.primary[700] }} />} sx={{ backgroundColor: colors.primary[50] }}><Typography sx={{ color: colors.primary[900], fontWeight: 700 }}>{salon.name}</Typography></AccordionSummary>
                  <AccordionDetails>
                    <PermissionMatrix groups={groups} value={grantsBySalon[salon.salon_id] || commonGrant} onChange={(next) => setGrantsBySalon({ ...grantsBySalon, [salon.salon_id]: next })} canGrantManagerManage={canGrantManagerManage} />
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}

          {sectionTitle(7, tr('pro_manager_access.form.steps.confirm', 'Vérifier puis confirmer'))}
          <Typography variant="body2" color="text.secondary">
            {tr('pro_manager_access.form.confirm_help', 'Vous pourrez modifier ou suspendre ces accès à tout moment.')}
          </Typography>
          {(validationError || error) && <Alert severity="error" sx={{ mt: 2 }}>{validationError || error}</Alert>}
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: colors.secondary[50] }}>
        <Button onClick={onClose} disabled={isSaving}>{tr('common.cancel', 'Annuler')}</Button>
        <Button type="submit" form="manager-access-form" variant="contained" disabled={isSaving}>
          {isSaving ? <CircularProgress size={22} /> : tr('pro_manager_access.form.confirm', 'Confirmer les accès')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManagerAccessForm;
